import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText, FileType2, FileType, Presentation } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { api, formatApiError, API } from "@/lib/api";
import { getToken } from "@/lib/api";

const MODULE_META = {
  command:    { label: "Command Center — Briefs",  color: "#0B7E8C" },
  rpm:        { label: "RPM Command Center",       color: "#0B7E8C" },
  onboarding: { label: "Onboarding Workflow",      color: "#6366F1" },
  hotline:    { label: "Nurse Hotline",            color: "#F59E0B" },
  fleet:      { label: "Fleet Management",         color: "#06B6D4" },
};

const FORMAT_META = {
  pdf:  { icon: FileType2,       label: "PDF",        color: "#DC2626" },
  xlsx: { icon: FileSpreadsheet, label: "Excel",      color: "#16A34A" },
  docx: { icon: FileType,        label: "Word",       color: "#2563EB" },
  pptx: { icon: Presentation,    label: "PowerPoint", color: "#EA580C" },
  csv:  { icon: FileText,        label: "CSV",        color: "#6B7280" },
};

const FORMAT_ORDER = ["pdf", "xlsx", "docx", "pptx", "csv"];

export default function Reports() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reports");
        setItems(r.data.reports || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  async function downloadReport(path, format) {
    setBusy(`${path}:${format}`);
    try {
      const token = getToken();
      const resp = await fetch(`${API}/reports/${path}?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)/i);
      const filename = m ? m[1] : `${path.replace("/", "_")}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error(e.message || "Download failed"); }
    finally { setBusy(null); }
  }

  const grouped = items.reduce((acc, it) => {
    (acc[it.module] = acc[it.module] || []).push(it);
    return acc;
  }, {});

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports & Exports" subtitle="Download operational reports as PDF, Excel, Word, PowerPoint or CSV — all use the H@H branded template." />
      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([module, reports]) => {
            const meta = MODULE_META[module] || { label: module, color: "#0B7E8C" };
            return (
              <section key={module} data-testid={`reports-module-${module}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                  <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</h2>
                  <div className="h-px flex-1 bg-[#E5E7EB]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {reports.map((r) => (
                    <div key={r.path} className="hah-card p-4" data-testid={`report-card-${r.path.replace("/", "-")}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Download className="w-4 h-4 text-[#0B7E8C]" />
                        <h3 className="text-sm font-semibold text-[#1F2937]">{r.label}</h3>
                      </div>
                      <div className="grid grid-cols-3 lg:grid-cols-5 gap-1.5">
                        {FORMAT_ORDER.map((f) => {
                          const Icon = FORMAT_META[f].icon;
                          const loading = busy === `${r.path}:${f}`;
                          return (
                            <Button
                              key={f}
                              size="sm"
                              variant="outline"
                              className="border-[#E5E7EB] px-2"
                              disabled={loading}
                              onClick={() => downloadReport(r.path, f)}
                              data-testid={`report-${r.path.replace("/", "-")}-${f}`}
                              title={`Download as ${FORMAT_META[f].label}`}
                            >
                              <Icon className="w-3.5 h-3.5 mr-1" style={{ color: FORMAT_META[f].color }} />
                              <span className="text-[11px]">{FORMAT_META[f].label}</span>
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
